import config from './config';

/**
 * Known utility link IDs used in `mobileMenu.utilityLinks`.
 */
export enum UtilityLinkId {
  StoreLocator = 'store-locator',
  CustomerService = 'customer-service',
  /** Shown only when the user is authenticated; triggers logout. */
  Logout = 'logout',
  /** Renders the country / language picker component. */
  CountrySelector = 'country-selector',
  membership = 'membership',
  trackPackage = 'track-package'
}

const menu = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      /** Sanuk Menu Config. */

      /**
       * The main menu settings. Note that by default, we pull the main menu
       * from the CMS. If you want to override this, set overrideCmsValues to
       * true and add your own links.
       */
      mainMenu: {
        showUtilityLinks: true,
        userButtonInHeader: false,
        showWishlistLink: true,
        overrideCmsValues: false,
        links: [{ id: '', label: '', href: '' }]
      },
      mobileMenu: {
        /**
         * Whether to show the utility links in the mobile menu.
         */
        showUtilityLinks: true,
        /**
         * Show the user button in the main header or the top utility bar.
         */
        userButtonInHeader: true,
        /**
         * Whether to sit the mobile menu below the header. If so, a portal
         * will be used to render the internal drawer.
         */
        sitBelowHeader: true,
        /**
         * Whether to show the navigation back button on the parent / root drawer.
         */
        showBackButtonOnParentDrawer: true,
        /**
         * Whether to show a close (X) button at the top-right of the drawer.
         */
        showCloseButton: false,
        /**
         * Whether to show the right-arrow icon on navigation items
         * that open inner drawers.
         */
        showNavArrow: true,
        /**
         * Whether to use accordion-style navigation instead of nested drawers.
         */
        isAccordionNavigation: false,
        /**
         * Whether to show the primary category name as a breadcrumb label
         * in sub-drawers.
         */
        showBreadcrumbLabel: false,
        /**
         * Whether to show footer utility links on inner (page 2) drawers
         * as well.
         */
        showUtilityLinksOnPage2: false,
        /**
         * Utility links shown in the mobile menu footer. Each brand can
         * override this array to control which links appear and their order.
         *
         * Standard link entries use `{ id, label, href, icon }` where `icon`
         * matches an IconTypes enum value (e.g. 'mapMarker').
         *
         * Reserved IDs with special rendering behavior:
         * - `'logout'` — shown only when authenticated, triggers logout.
         * - `'country-selector'` — renders the country/language picker.
         */
        utilityLinks: [
          {
            id: UtilityLinkId.StoreLocator,
            label: '',
            href: '/storelocator',
            icon: 'mapMarker'
          },
          {
            id: UtilityLinkId.CustomerService,
            label: '',
            href: '/help-center',
            icon: 'helpChatLight'
          },
          { id: UtilityLinkId.Logout, label: '', href: '', icon: 'angleLeft' },
          { id: UtilityLinkId.CountrySelector, label: '', href: '', icon: '' }
        ]
      },
      /** Use the data model marketing banner and not the regular content. */
      useDataModelBanner: true
    }
  },
  'AHNU-US': {
    default: {
      mainMenu: {
        showUtilityLinks: false,
        userButtonInHeader: true,
        showWishlistLink: false,
        overrideCmsValues: true,
        links: [
          { id: 'men-shoes', label: 'Men’s Shoes', href: '/c/men/shoes' },
          { id: 'women-shoes', label: 'Women’s Shoes', href: '/c/women/shoes' },
          { id: 'about', label: 'About AHNU', href: '/about' },
          { id: 'stores', label: 'Store Locator', href: '/storelocator' }
        ]
      },
      mobileMenu: { showUtilityLinks: false, sitBelowHeader: false }
    }
  },
  'UGG-COLLAB-US': {
    default: {
      mainMenu: {
        overrideCmsValues: true,
        links: [{ id: '1170430', label: 'All', href: '/p/1170430' }]
      }
    }
  },
  'TEVA-US': {
    default: {
      mainMenu: {
        showWishlistLink: false,
        overrideCmsValues: true,
        links: [
          { id: 'women', label: 'Women', href: '/p/1090969' },
          { id: 'men', label: 'Men', href: '/c/all' },
          { id: 'kids', label: 'Kids', href: '/p/1090969' },
          { id: 'inside-teva', label: 'Inside Teva', href: '/about' }
        ]
      },
      mobileMenu: {
        showBackButtonOnParentDrawer: false,
        utilityLinks: [
          {
            id: UtilityLinkId.CustomerService,
            label: '',
            href: '/help-center',
            icon: 'helpChatLight'
          },
          {
            id: UtilityLinkId.StoreLocator,
            label: '',
            href: '/storelocator',
            icon: 'mapMarker'
          },
          {
            id: UtilityLinkId.Logout,
            label: '',
            href: '',
            icon: 'arrowRightFromBracket'
          },
          { id: UtilityLinkId.CountrySelector, label: '', href: '', icon: '' }
        ]
      }
    }
  },
  'HOKA-US': {
    default: {
      mainMenu: {
        showWishlistLink: false,
        userButtonInHeader: true,
        overrideCmsValues: true,
        links: [{ id: '1162012', label: 'All', href: '/p/1162012' }]
      },
      mobileMenu: {
        showBackButtonOnParentDrawer: false,
        showCloseButton: true,
        showNavArrow: false,
        isAccordionNavigation: true,
        showBreadcrumbLabel: true,
        showUtilityLinksOnPage2: true,
        utilityLinks: [
          {
            id: UtilityLinkId.membership,
            label: '',
            href: '/membership/',
            icon: 'circleStar'
          },
          {
            id: UtilityLinkId.trackPackage,
            label: '',
            href: '/login/',
            icon: 'box'
          },
          {
            id: UtilityLinkId.StoreLocator,
            label: '',
            href: '/storelocator',
            icon: 'locationDot'
          },
          {
            id: UtilityLinkId.CustomerService,
            label: '',
            href: '/help-center',
            icon: 'comments'
          },
          { id: UtilityLinkId.Logout, label: '', href: '', icon: 'angleLeft' },
          { id: UtilityLinkId.CountrySelector, label: '', href: '', icon: '' }
        ]
      }
    }
  },
  'UGG-US': {
    default: {
      mainMenu: {
        showWishlistLink: false,
        userButtonInHeader: true,
        overrideCmsValues: true,
        links: [{ id: '1175209', label: 'All', href: '/p/1175209' }]
      }
    }
  }
});

export default menu;
